import React from 'react';
import { createRoot } from 'react-dom/client';
import App from '../src/components/App/App';
import { CookiesProvider } from 'react-cookie';
import { BrowserRouter } from 'react-router-dom';
import { UserProvider } from './library/UserContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import GlassboxInitializer from './components/GlassboxInitializer';
import './index.scss';
import { ThemeProvider } from '@emotion/react';
import { createTheme } from '@mui/material';
import { trackButtonClick } from './helpers/GlassboxTracking';
const container = document.getElementById('react-root');
const root = createRoot(container);
const _env = process.env.REACT_APP_ENV === 'production' ? '' : `/${process.env.REACT_APP_ENV}`;
const baseName = _env;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  },
});
const theme = createTheme({
  typography: {
    fontSize: 14,
    fontFamily: 'Optum Sans',
  },
});
// Global button click tracking for Glassbox
document.querySelector('body')?.addEventListener('click', (e) => {
  try {
    const target = e.target as HTMLElement;
    // Find the button element (clicked element or closest parent button)
    const button = target.closest('button') || 
                 (target.tagName === 'BUTTON' ? target : null) ||
                 target.closest('[role="button"]') ||
                 target.closest('[role="tab"]') ||
                 target.closest('[role="menuitem"]') ||
                 target.closest('[role="option"]') ||
                 target.closest('.MuiButtonBase-root') ||
                 target.closest('[class*="clickable"]') ||
                 target.closest('a[href]');
  if (button) {
    // Check for generic MUI base classes that should not be tracked
    const isGenericMuiClass = (el: Element): boolean => {
      const classNames = el.className;
      if (typeof classNames !== 'string') return false;
      
      // Only skip if element has ONLY these generic base classes (no other specific classes)
      const genericOnlyClasses = [
        'MuiButtonBase-root',
        'MuiButton-root',
        'MuiIconButton-root',
        'PrivateSwitchBase-root',
        'MuiSwitch-root',
        'MuiCheckbox-root',
        'MuiRadio-root'
      ];
      
      const classes = classNames.split(' ').filter(cls => cls.length > 0);
      
      // Check if it only has generic classes (and css-* classes)
      const hasOnlyGeneric = classes.every(cls => 
        genericOnlyClasses.includes(cls) || 
        cls.startsWith('css-')
      );
      
      if (hasOnlyGeneric && classes.some(cls => genericOnlyClasses.includes(cls))) {
        return true;
      }
      
      return false;
    };
    
    // Skip generic MUI buttons without meaningful identification
    if (isGenericMuiClass(button)) {
      return;
    }
    
    // Skip date picker component buttons (month/day/year selectors)
    const isDatePickerComponent = 
      button.closest('.MuiPickersCalendar-root') ||
      button.closest('.MuiPickersDay-root') ||
      button.closest('.MuiDateCalendar-root') ||
      button.closest('.MuiMonthCalendar-root') ||
      button.closest('.MuiYearCalendar-root') ||
      button.closest('[class*="PrivatePickersMonth"]') ||
      button.closest('[class*="PrivatePickersYear"]') ||
      button.closest('[class*="MuiPickersCalendarHeader"]');
    
    if (isDatePickerComponent) {
      return; // Skip tracking date picker internal components
    }
    
    // Helper function to find label for form controls (checkbox, switch, radio)
    const findFormControlLabel = (element: HTMLElement): string | null => {
      // Check if this is inside a FormControlLabel
      const formControlLabel = element.closest('.MuiFormControlLabel-root');
      if (formControlLabel) {
        const labelSpan = formControlLabel.querySelector('.MuiFormControlLabel-label');
        if (labelSpan?.textContent?.trim()) {
          return labelSpan.textContent.trim();
        }
      }
      
      // Look for associated label by htmlFor or aria-labelledby
      const inputId = element.getAttribute('id');
      if (inputId) {
        const associatedLabel = document.querySelector(`label[for="${inputId}"]`);
        if (associatedLabel?.textContent?.trim()) {
          return associatedLabel.textContent.trim();
        }
      }
      
      const ariaLabelledBy = element.getAttribute('aria-labelledby');
      if (ariaLabelledBy) {
        const labelElement = document.getElementById(ariaLabelledBy);
        if (labelElement?.textContent?.trim()) {
          return labelElement.textContent.trim();
        }
      }
      
      return null;
    };
    
    // Helper function to extract the primary label from button text
    const extractButtonLabel = (element: HTMLElement): string | null => {
      // First check if this is a form control (checkbox, switch, radio)
      const formControlLabel = findFormControlLabel(element);
      if (formControlLabel) {
        return formControlLabel;
      }
      
      // Try to find a label element or the first text-only child
      const labelElement = element.querySelector('label, [class*="label"]');
      if (labelElement?.textContent?.trim()) {
        return labelElement.textContent.trim();
      }
      
      // Get only the first direct text node (ignoring nested elements)
      for (const node of Array.from(element.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text && text.length > 0 && text.length < 100) {
            return text;
          }
        }
      }
      
      // Fallback: get all text but clean it aggressively
      const allText = element.textContent?.trim();
      if (!allText) return null;
      
      // Remove common unwanted patterns
      let cleaned = allText
        .replace(/\d+\s+Selected/gi, '')
        .replace(/Exclude:\s*(Yes|No)/gi, '')
        .replace(/\+\s*\d+/g, '')
        .replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},?\s+\d{2,4}\b/gi, '')
        .replace(/\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/g, '')
        .replace(/[-–—]\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/gi, '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\d+\s*$/g, '')
        .replace(/\s+/g, ' ')
        .replace(/[,\-–—:]+$/, '')
        .trim();
      
      return cleaned.length > 0 && cleaned.length < 100 ? cleaned : null;
    };
    
    // Helper function to separate section header from button value
    const separateSectionFromValue = (text: string): { section: string | null; value: string } => {
      // Common section patterns throughout the FrontDoor application
      const sectionPatterns = [
        // Dashboard sections
        'Dashboard',
        'Member',
        'Organization',
        'Health System',
        // Advanced Reporting sections
        'Advanced Reporting',
        'My Reports',
        'Shared Reports',
        'Report Templates',
        'Advanced Report Templates',
        'Workspace',
        'Folder',
        'Report Name',
        'Report Type',
        'Report',
        // User Management sections
        'User List',
        'User Data Configuration',
        'User Profile',
        'User',
        'Role',
        'Role Type',
        'Permission',
        'Permissions',
        'Status',
        'Specialty',
        'Specialties',
        'Access',
        // Settings sections
        'General Settings',
        'Settings',
        'Configuration',
        'Preferences',
        // Module sections
        'Provider Review',
        'Physician Review',
        'Hospital Quality',
        'Hospital Utilization',
        'Quality and Utilization',
        'Spotlight',
        'PowerBI',
        // Action sections
        'Create',
        'Edit',
        'Delete',
        'Share',
        'Export',
        'Download',
        'Save',
        'Cancel',
        'Submit',
        'Apply',
        'Clear',
        'Reset',
        'Search',
        'Filter',
        'Sort',
        'View',
        'Launch',
        'Close'
      ];
      
      for (const pattern of sectionPatterns) {
        if (text.startsWith(pattern)) {
          const value = text.substring(pattern.length).trim();
          if (value.length > 0) {
            return { section: pattern, value: value };
          }
        }
      }
      
      return { section: null, value: text };
    };
    
    // Extract button name from various sources in priority order
    const ariaLabel = button.getAttribute('aria-label')?.trim();
    const title = button.getAttribute('title')?.trim();
    const dataButtonName = button.getAttribute('data-button-name')?.trim();
    const extractedText = extractButtonLabel(button as HTMLElement);
    const buttonId = button.getAttribute('id');
    
    // Check if extracted text contains a section header
    let detectedSection: string | null = null;
    let buttonValue = extractedText;
    
    if (extractedText) {
      const separated = separateSectionFromValue(extractedText);
      if (separated.section) {
        detectedSection = separated.section;
        buttonValue = separated.value;
      }
    }
    
    // Filter out generic MUI classes
    const getCleanClassName = (): string | null => {
      const buttonClass = button.getAttribute('class');
      if (!buttonClass) return null;
      
      const classes = buttonClass.split(' ');
      for (const cls of classes) {
        if (!cls.startsWith('Mui') && !cls.startsWith('css-') && !cls.startsWith('Private') && cls.length > 0) {
          return `button-${cls}`;
        }
      }
      return null;
    };
    
    let buttonName = 
      ariaLabel ||
      title ||
      dataButtonName ||
      buttonValue ||
      buttonId ||
      getCleanClassName() ||
      null;
    // Skip tracking if no meaningful name found
    if (!buttonName) {
      return;
    }
    // Helper function to find main header in drawer
    const findDrawerMainHeader = (drawer: Element): string | null => {
      const dialogTitle = drawer.querySelector('.MuiDialogTitle-root, [class*="DialogTitle"]');
      if (dialogTitle?.textContent?.trim()) {
        return dialogTitle.textContent.trim();
      }
      
      const headerElements = drawer.querySelectorAll('h4, h5, h6');
      for (const header of Array.from(headerElements)) {
        const headerText = header.textContent?.trim();
        if (headerText && headerText.length > 0 && headerText.length < 100) {
          return headerText;
        }
      }
      
      return null;
    };
    
    // Helper function to find section header near button
    const findSectionHeader = (element: HTMLElement, drawer: Element, existingParts: string[]): string | null => {
      let currentElement = element.parentElement;
      
      while (currentElement && drawer.contains(currentElement)) {
        const sectionHeader = currentElement.querySelector(':scope > h6, :scope > h5');
        if (sectionHeader?.textContent?.trim()) {
          const sectionText = sectionHeader.textContent.trim();
          if (sectionText.length > 0 && sectionText.length < 50 && !existingParts.includes(sectionText)) {
            return sectionText;
          }
        }
        
        const labelHeader = currentElement.querySelector(':scope > [class*="heading"], :scope > label');
        if (labelHeader?.textContent?.trim()) {
          const labelText = labelHeader.textContent.trim();
          if (labelText.length > 0 && labelText.length < 50 && !existingParts.includes(labelText)) {
            return labelText;
          }
        }
        
        currentElement = currentElement.parentElement;
      }
      
      return null;
    };
    
    // Helper function to find dialog title
    const findDialogTitle = (element: HTMLElement): string | null => {
      const dialog = element.closest('[role="alertdialog"], .MuiDialog-root');
      if (!dialog) return null;
      
      const dialogTitle = dialog.querySelector('.MuiDialogTitle-root, [class*="DialogTitle"]');
      return dialogTitle?.textContent?.trim() || null;
    };
    
    // Helper function to find data context attributes
    const findDataContext = (element: HTMLElement): string | null => {
      let parent = element.parentElement;
      while (parent) {
        const dataContext = parent.getAttribute('data-context') || parent.getAttribute('data-section');
        if (dataContext) return dataContext;
        parent = parent.parentElement;
      }
      return null;
    };
    
    // Helper function to find dropdown label for menu items/options
    const findDropdownLabel = (element: HTMLElement): string | null => {
      // Check if this is a dropdown option/menu item
      const isDropdownOption = element.closest('[role="option"], [role="menuitem"], .MuiMenuItem-root, .MuiMenu-list');
      if (!isDropdownOption) return null;
      
      // Find the associated select/dropdown element
      const menu = element.closest('.MuiMenu-root, .MuiPopover-root, [role="menu"], [role="listbox"]');
      if (!menu) return null;
      
      // Get the aria-labelledby from the menu/listbox
      const ariaLabelledBy = menu.getAttribute('aria-labelledby');
      if (ariaLabelledBy) {
        const labelElement = document.getElementById(ariaLabelledBy);
        if (labelElement?.textContent?.trim()) {
          return labelElement.textContent.trim();
        }
      }
      
      // Try to find the label by looking for FormControl with label
      // Search in the DOM for recently opened select/dropdown
      const allSelects = document.querySelectorAll('.MuiSelect-select, [role="combobox"], [aria-haspopup="listbox"]');
      for (const select of Array.from(allSelects)) {
        // Check if this select is aria-expanded or has open attribute
        const isExpanded = select.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          // Find FormControl parent
          const formControl = select.closest('.MuiFormControl-root');
          if (formControl) {
            // Look for label sibling or child
            const label = formControl.querySelector('label');
            if (label?.textContent?.trim()) {
              return label.textContent.trim();
            }
            
            // Look for label as previous sibling
            const prevSibling = formControl.previousElementSibling;
            if (prevSibling?.tagName === 'LABEL' && prevSibling.textContent?.trim()) {
              return prevSibling.textContent.trim();
            }
          }
          
          // Look for label before the select
          const parent = select.parentElement;
          if (parent) {
            const labelInParent = parent.querySelector('label');
            if (labelInParent?.textContent?.trim()) {
              return labelInParent.textContent.trim();
            }
          }
        }
      }
      
      return null;
    };
    
    // Find the context/header where the button is located
    const getContextHeader = (element: HTMLElement): string | null => {
      const contextParts: string[] = [];
      
      // Try to find context from drawer
      const drawer = element.closest('.MuiDrawer-root, [role="dialog"]');
      if (drawer) {
        const mainHeader = findDrawerMainHeader(drawer);
        if (mainHeader) contextParts.push(mainHeader);
        
        const sectionHeader = findSectionHeader(element, drawer, contextParts);
        if (sectionHeader) contextParts.push(sectionHeader);
      }
      
      // Fallback to dialog if no drawer context found
      if (contextParts.length === 0) {
        const dialogTitle = findDialogTitle(element);
        if (dialogTitle) contextParts.push(dialogTitle);
      }
      
      // Final fallback to data attributes
      if (contextParts.length === 0) {
        const dataContext = findDataContext(element);
        if (dataContext) contextParts.push(dataContext);
      }
      
      return contextParts.length > 0 ? contextParts.join(' - ') : null;
    };
    // Get the context header
    const contextHeader = getContextHeader(button as HTMLElement);
    
    // Check if this is a dropdown option and get its label
    const dropdownLabel = findDropdownLabel(button as HTMLElement);
    if (dropdownLabel && !detectedSection) {
      // Use dropdown label as the section if no section was detected from button text
      detectedSection = dropdownLabel;
    }
    
    // Helper function to format text for button name (keep spaces for tile context)
    const formatForButtonName = (text: string): string => {
      return text
        .trim()
        .replace(/\s+/g, ' ')  // Normalize spaces
        .replace(/[^\w\s-]/g, '');  // Remove special characters except hyphens and spaces
    };
    
    // Helper function to find tile context (for dashboard tiles)
    const findTileContext = (element: HTMLElement): { pageHeader: string | null; tileName: string | null } => {
      // Look for the tile container (MUI Card or similar) - try multiple approaches
      let tileContainer = element.closest('.MuiCard-root, .MuiPaper-root, [class*="tile"], [class*="card"], [class*="Card"]');
      
      // If not found, try looking for parent divs with specific patterns
      if (!tileContainer) {
        let parent = element.parentElement;
        let depth = 0;
        while (parent && depth < 10) {
          const className = parent.className || '';
          
          if (typeof className === 'string' && 
              (className.includes('Card') || 
               className.includes('tile') || 
               className.includes('Paper') ||
               className.includes('MuiCard') ||
               className.includes('MuiPaper'))) {
            tileContainer = parent;
            break;
          }
          parent = parent.parentElement;
          depth++;
        }
      }
      
      if (!tileContainer) {
        return { pageHeader: null, tileName: null };
      }
      
      // Find tile name/header within the tile - try multiple selectors
      let tileName: string | null = null;
      
      // Try CardHeader title first
      const cardHeaderTitle = tileContainer.querySelector('.MuiCardHeader-title, [class*="CardHeader-title"]');
      if (cardHeaderTitle?.textContent?.trim()) {
        tileName = cardHeaderTitle.textContent.trim();
      }
      
      // Try h5, h6 headers
      if (!tileName) {
        const tileHeader = tileContainer.querySelector('h5, h6, h4, h3, h2');
        if (tileHeader?.textContent?.trim()) {
          tileName = tileHeader.textContent.trim();
        }
      }
      
      // Try any element with title/heading in class name
      if (!tileName) {
        const titleElement = tileContainer.querySelector('[class*="title"], [class*="Title"], [class*="heading"], [class*="Heading"]');
        if (titleElement?.textContent?.trim()) {
          tileName = titleElement.textContent.trim();
        }
      }
      
      // Try the first child with text (might be the title)
      if (!tileName) {
        const children = Array.from(tileContainer.children);
        for (const child of children) {
          const text = child.textContent?.trim();
          if (text && text.length > 0 && text.length < 100) {
            // Skip if it's the button itself or contains the button
            if (!child.contains(element)) {
              tileName = text;
              break;
            }
          }
        }
      }
      
      // Find page-level header by looking for the closest panel that contains this tile
      let pageHeader: string | null = null;
      
      // Strategy 1: Walk up from tile container to find the closest panel with panel-header
      let parent = tileContainer.parentElement;
      let depth = 0;
      while (parent && depth < 10) {
        // Check if this parent is a panel container
        const className = parent.className || '';
        if (typeof className === 'string' && className.includes('panel')) {
          // Found a panel, now look for its header
          const panelHeader = parent.querySelector('.panel-header, [class*="panel-header"]');
          if (panelHeader) {
            // Get the first child div which typically contains the panel name
            const headerText = panelHeader.querySelector('div');
            if (headerText?.textContent?.trim()) {
              pageHeader = headerText.textContent.trim();
              break;
            }
            // Fallback to panel header text content
            if (panelHeader.textContent?.trim()) {
              pageHeader = panelHeader.textContent.trim();
              break;
            }
          }
        }
        parent = parent.parentElement;
        depth++;
      }
      
      // Strategy 2: Look for the closest parent with panel-header sibling
      if (!pageHeader) {
        parent = tileContainer.parentElement;
        depth = 0;
        while (parent && depth < 10) {
          // Look for panel-header as a sibling or within parent
          const panelHeader = parent.querySelector(':scope > .panel-header');
          if (panelHeader) {
            const headerDiv = panelHeader.querySelector('div');
            if (headerDiv?.textContent?.trim()) {
              pageHeader = headerDiv.textContent.trim();
              break;
            }
          }
          parent = parent.parentElement;
          depth++;
        }
      }
      
      // Strategy 3: Look for h1 elements (fallback)
      if (!pageHeader) {
        const h1Elements = document.querySelectorAll('h1');
        if (h1Elements.length > 0) {
          pageHeader = h1Elements[0].textContent?.trim() || null;
        }
      }
      
      // Strategy 4: Look for elements with page/panel/title related classes
      if (!pageHeader) {
        const titleSelectors = [
          '[class*="PageTitle"]', '[class*="pageTitle"]', '[class*="page-title"]',
          '[class*="panel-header"]', '[class*="PanelHeader"]', '[class*="panelHeader"]',
          '[class*="PageHeader"]', '[class*="pageHeader"]', '[class*="page-header"]',
          '[class*="main-title"]', '[class*="MainTitle"]'
        ];
        
        for (const selector of titleSelectors) {
          const element = document.querySelector(selector);
          if (element?.textContent?.trim()) {
            pageHeader = element.textContent.trim();
            break;
          }
        }
      }
      
      // Strategy 5: Look for h2 elements outside the tile container
      if (!pageHeader) {
        const allH2 = document.querySelectorAll('h2');
        for (const h2 of Array.from(allH2)) {
          // Make sure this h2 is NOT inside the tile container
          if (!tileContainer.contains(h2)) {
            const text = h2.textContent?.trim();
            if (text && text.length > 0 && text.length < 200) {
              pageHeader = text;
              break;
            }
          }
        }
      }
      
      return { pageHeader, tileName };
    };
    
    // Special handling for Reports pages - extract context from URL and page
    const pathname = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check if button is within a tile (for dashboard tiles like Quality and Utilization)
    const tileContext = findTileContext(button as HTMLElement);
    
    if (tileContext.tileName) {
      // This is a tile button - format: "Panel Header - Tile Name - Button Name"
      const tileNameParts: string[] = [];
      
      // Add page header if found (this is the panel header)
      if (tileContext.pageHeader) {
        tileNameParts.push(formatForButtonName(tileContext.pageHeader));
      }
      
      // Add tile name
      tileNameParts.push(formatForButtonName(tileContext.tileName));
      
      // Add button name
      tileNameParts.push(formatForButtonName(buttonName));
      
      // Join with " - " for readability
      buttonName = tileNameParts.join(' - ');
    } else if ((pathname.includes('/reports') || pathname.includes('/my-reports') || pathname.includes('/shared-reports')) && !contextHeader) {
      const reportContextParts: string[] = [];
      
      // Determine report type from pathname
      if (pathname.includes('/my-reports')) {
        reportContextParts.push('MyReports');
      } else if (pathname.includes('/shared-reports')) {
        reportContextParts.push('SharedReports');
      }
      
      // Get report name from URL parameters and extract only the actual report name
      const reportNameParam = urlParams.get('report_name');
      if (reportNameParam) {
        // Report name format: "DEVENG1|dev|kallam_priya@optum.com|Test_SubFolder"
        // We only want the last part (Test_SubFolder)
        const parts = reportNameParam.split('|');
        const actualReportName = parts[parts.length - 1]; // Get the last part
        if (actualReportName && actualReportName.trim()) {
          reportContextParts.push(formatForButtonName(actualReportName));
        }
      }
      
      // If we have report context, use it
      if (reportContextParts.length > 0) {
        reportContextParts.push(buttonName);
        buttonName = reportContextParts.join(' - ');
      } else {
        // Fallback: try to get report name from page title or h1
        const pageTitle = document.querySelector('h1, h2, [class*="title"]');
        const pageTitleText = pageTitle?.textContent?.trim();
        
        if (pageTitleText && pageTitleText.length < 100) {
          buttonName = `${formatForButtonName(pageTitleText)} - ${buttonName}`;
        }
      }
    } else {
      // Build the final button name with context hierarchy using " - " format
      const parts: string[] = [];
      
      if (contextHeader) {
        parts.push(formatForButtonName(contextHeader));
      }
      
      if (detectedSection) {
        parts.push(formatForButtonName(detectedSection));
      }
      
      parts.push(formatForButtonName(buttonName));
      
      // Join all parts with " - ": "Context Header - Section - Button Name"
      buttonName = parts.join(' - ');
    }

    const rawButtonText = (button as HTMLElement).textContent?.trim().toLowerCase() || '';
    const ariaButtonLabel = button.getAttribute('aria-label')?.toLowerCase() || '';
    const normalizedButtonName = buttonName.toLowerCase();
    const isCreateReportPage = pathname.includes('/v2/advanced-reporting/create-report');
    const isCreateReportActionClick =
      rawButtonText.includes('create report') ||
      ariaButtonLabel.includes('create report') ||
      normalizedButtonName.includes('rows per page');

    // The Create Report row action on this page is tracked separately; skip global click tracking noise.
    if (isCreateReportPage && isCreateReportActionClick) {
      return;
    }

    const pageDetails = {
      page_title: document.title,
      page_url: window.location.href,
      page_pathname: window.location.pathname,
      page_search: window.location.search,
      page_hash: window.location.hash,
      context_header: contextHeader || ''
    };
    
    // Track the button click
    trackButtonClick(buttonName, pageDetails, button as HTMLElement);
  }
  } catch (error) {
    // Silent error handling - tracking failures should never break the app
    console.error('Button tracking error:', error);
  }
});
root.render(
  <BrowserRouter basename={baseName}>
    <CookiesProvider>
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <ThemeProvider theme={theme}>
            <GlassboxInitializer />
            <App />
          </ThemeProvider>
        </UserProvider>
      </QueryClientProvider>
    </CookiesProvider>
  </BrowserRouter>,
);